
import { Component, TemplateRef, computed, inject, input, output, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { delay, filter, firstValueFrom, of, tap } from 'rxjs';
import { AuthenticationService } from '../../authentication/authentication.service';
import { BillingChargeService, CreditAction } from '../../core/billing-charge.service';
import { FirebaseEventService } from '../../authentication/firebase.event.service';
import { MceApiService, ProjectDto, UserDto } from '../../core/mceapi.client.generated';
import { InfoMessageComponent } from '../../shared/components/info-message/info-message.component';
import { MceDataTableComponent } from '../../shared/components/mce-data-table/mce-data-table.component';
import { MceSaveCancelComponent } from '../../shared/components/mce-save-cancel/mce-save-cancel.component';
import { MceSaveCancelEvent } from '../../shared/components/mce-save-cancel/mce-save-cancel.component';
import { MceSaveCancelConfig } from '../../shared/components/mce-save-cancel/mce-save-cancel.component';
import { DialogService } from '../../shared/dialogs/dialog.service';
import { UserEmailModel } from '../../shared/dialogs/user-email/user-email.component';
import { isSome } from '../../shared/functions/functional';
import { ProjectHeaderComponent } from '../project-header/project-header.component';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';


interface DataTable {
  headers: { [key: string]: string; };
  displayedColumns: string[];
  templates: { [key: string]: TemplateRef<any>; };
  showSelect: boolean;
  data: UserDto[];
}

@Component({
  selector: 'project-users',
  imports: [ProjectHeaderComponent, MatToolbarModule, MatButtonModule, InfoMessageComponent,
    MceSaveCancelComponent, MceDataTableComponent, MatIconModule, MatProgressBarModule, MatProgressSpinnerModule],
  templateUrl: './project-users.component.html',
})
export class ProjectUsersComponent {

  project = input.required<ProjectDto>();
  cancelClick = output();

  photo = viewChild.required<TemplateRef<any>>('photo');
  rowActions = viewChild.required<TemplateRef<any>>('rowActions');

  private authenticationService = inject(AuthenticationService);
  private billingChargeService = inject(BillingChargeService);
  private mceApiService = inject(MceApiService);
  private dialogService = inject(DialogService);
  private firebaseEventService = inject(FirebaseEventService);

  processing = signal(false);

  usersResource = rxResource({
    params: () => ({ projectUid: this.project().uid }),
    stream: ({ params }) =>
      params.projectUid ? this.mceApiService.v1ProjectsUsers(params.projectUid).pipe(delay(2000)) : of([]),
  });
  users = computed(() => this.usersResource.value() ?? []);

  protected readonly dataTable = computed<DataTable>(() => {
    return {
      headers: { 'photo': ' ', 'displayName': 'User', 'email': 'Email Address', 'actions': ' ' },
      displayedColumns: ['photo', 'displayName', 'email', 'actions',],
      templates: {'photo': this.photo(), 'actions': this.rowActions() },
      showSelect: false,
      data: this.users(),
    };
  });

  protected readonly viewModel = computed(() => {

    return {

      dataTable: this.dataTable(),

      disableRemoveUser: (this.dataTable().data?.length ?? 0) === 1,

      showBack: true, 
      showLoading: this.usersResource.isLoading(),
      showSpinner: this.processing(),
    };
  });

  protected get vm() { return this.viewModel(); }

  protected readonly saveCancelConfig = computed<MceSaveCancelConfig>(() => ({
    back: { show: this.vm.showBack, enable: true }
  }));

  async addUser() {
    const result = await this.dialogService.userEmail();

    if (!result) {
      return;
    }

    const model = result as UserEmailModel;
    const projectUid = this.project().uid;

    if (!projectUid) {
      return;
    }

    try {
      this.processing.set(true);

      const chargeQuote = await firstValueFrom(this.billingChargeService.getQuote({
        action: CreditAction.ProjectAddUser,
      }));

      const canProceed = await this.billingChargeService.ensureCanProceed(chargeQuote);
      if (!canProceed) return;

      await firstValueFrom(this.mceApiService.v1ProjectsUsersAdd({
        projectUid,
        userEmail: model.email,
      }));

      await firstValueFrom(this.billingChargeService.applyCharge(chargeQuote));
      this.usersResource.reload();
    } finally {
      this.processing.set(false);
    }
  }


  removeUser(user: UserDto) {
    const currentUser = this.authenticationService.currentUser();
    const isCurrentUser = isSome(currentUser) && user.uid === currentUser.value.uid;

    if (!isCurrentUser) {
      this.removeUserAction(user);
    } else {
      this.dialogService.confirm(
        'Remove User',
        `You are about to remove yourself from this project
        and will lose access to the project. Are you sure?`)
        .pipe(
          filter(result => result),  
          tap(() => this.removeUserAction(user))
        ).subscribe();
    }
  }

  removeUserAction(user: UserDto) {
    const project = this.project();
    const projectUid = project.uid;
    if (!projectUid || !user.uid) return;

    this.processing.set(true);
    this.mceApiService.v1ProjectsUsersRemove(projectUid, user.uid).pipe(
      tap(() => {
        this.usersResource.set(this.users().filter(x => x.uid !== user.uid));
        this.firebaseEventService.logRemoveProjectUserEvent(
          this.authenticationService.currentUser(), project, user);
        this.processing.set(false);
      })).subscribe();
  }


  onCancel() { this.cancelClick.emit(); }

  onSaveCancelAction(event: MceSaveCancelEvent): void {
    if (event.type === 'back') {
      this.onCancel();
    }
  }

}
